import {Component, Injector, OnInit, ViewChild} from '@angular/core';
import {FormControl} from "@angular/forms";
import {DateRange} from "@angular/material/datepicker";
import {ImportNewTransactionsConfig} from "@features/accounts/run-tenant-payouts/model/run-payouts.model";
import {RunPayoutsConfigService} from "@features/accounts/run-tenant-payouts/service/run-payouts-config.service";
import {ConsoleContextService} from "@security/console-context.service";
import {LoadingService} from "@ui-kit/framework/util/service/loading.service";
import {
  CustomDateRangeChangeEventModel
} from "@ui-kit/webkit/components/custom-date-filter/custom-date-range-change-event.model";
import {CustomDateRangeModel, TimeFrames} from "@ui-kit/webkit/components/custom-date-filter/custom-date-range.model";
import {lastValueFrom} from "rxjs";
import {BasePageComponent} from "../../../core/components/page-content/base-page.component";
import {PageHeaderTab} from "../../../core/components/page-header/page-header-tabs.interface";
import {PanelType} from "../../../shared/components/info-panel/info-panel.component";
import {DateUtilsService} from "../../../shared/services/dateUtils.service";
import {UtilsService} from "../../../shared/services/utils.service";
import {PaymentConfigRequest, TransactionSource} from "../../leasing/leasing.model";
import {PaymentConfigsService} from "../../settings/service/payment-configs.service";
import {RetailerTradingReportComponent} from "./retailer-trading-report/retailer-trading-report.component";
import {TotalTradingReportComponent} from "./total-trading-report/total-trading-report.component";
import {RetailerTradingReport, TotalTradingReport} from "./trading-report.interface";
import {TradingReportService} from "./trading-report.service";

@Component({
  selector: 'app-trading-report',
  templateUrl: './trading-report.component.html',
  styleUrls: ['./trading-report.component.scss']
})
export class TradingReportComponent extends BasePageComponent implements OnInit {
  @ViewChild('retailerReport') retailerReport!: RetailerTradingReportComponent;
  @ViewChild('totalReport') totalReport!: TotalTradingReportComponent;

  pageHeader: Map<"ALL" | "CASH", { section: string, title: string, description: string }> = new Map([
    ["CASH", {
      section: 'Cash Trading Report',
      title: 'Cash Trading Report',
      description: 'This trading report allows you to see how the total sales received per trading day for each different payment method. You can use this page to get the latest trading data for a shared point-of-sale.',
    }],
    [
      "ALL", {
      section: 'Daily Trading Report',
      title: 'Daily Trading Report',
      description: 'This trading report allows you to see how the total cash sales received per trading day. You can use this page to get the latest trading data for a shared point-of-sale.',
    },
    ]
  ]);
  paymentMethodMode: 'ALL' | 'CASH' = 'ALL';

  importMessage: string | null = null;
  refreshMessage = 'Please select a point-of-sale config and date range to display the current trading data.';
  showTables = false;

  paymentConfigType: FormControl<'OFFERING' | 'SHARED' | null> = new FormControl<'OFFERING' | 'SHARED' | null>(null);
  offeringPaymentConfigs: PaymentConfigRequest[] = [];
  sharedPaymentConfigs: PaymentConfigRequest[] = [];

  selectablePaymentConfigs: PaymentConfigRequest[] = [];
  selectedConfig: FormControl<string | null> = new FormControl<string | null>(null);

  latestDate: Date | null = null;

  dateRange: DateRange<Date | null> = new DateRange<Date | null>(null, null);
  dateFilters: CustomDateRangeModel[] = [
    new CustomDateRangeModel('1', 7, TimeFrames.DAYS, 'Last 7 days'),
    new CustomDateRangeModel('2', 1, TimeFrames.MONTH, 'Last month', false)
  ]

  retailerTradingReport: RetailerTradingReport | null = null;
  totalTradingReport: TotalTradingReport | null = null;

  activeTab: string = "linked_sales";
  tabs: PageHeaderTab[] = [
    {
      name: 'Linked Sales',
      isActive: true,
      code: "linked_sales",
    },
    {
      name: 'Unlinked Sales',
      isActive: false,
      code: "unlinked_sales",
      badge: null,
      disabled: true
    },
  ];

  canUserImportData = false;
  defaultImportConfig: ImportNewTransactionsConfig | null = null;

  constructor(private paymentConfigsService: PaymentConfigsService,
              private reportService: TradingReportService,
              public utils: UtilsService,
              public dateUtils: DateUtilsService,
              public loader: LoadingService,
              private context: ConsoleContextService,
              private payoutsConfigService: RunPayoutsConfigService,
              injector: Injector) {
    super(injector)
    this.paymentMethodMode = this.data.paymentMethod;
  }

  ngOnInit() {
    this.loadOfferingPaymentConfigs();
    this.loadSharedLeasePaymentConfigs();
    this.loadImportConfig();

    this.paymentConfigType.valueChanges.subscribe(value => {
      this.selectedConfig.setValue(null);
      this.retailerTradingReport = null;
      this.totalTradingReport = null;
      switch (value) {
        case "OFFERING":
          this.selectablePaymentConfigs = [...this.offeringPaymentConfigs];
          break;
        case "SHARED":
          this.selectablePaymentConfigs = [...this.sharedPaymentConfigs];
          break;
        case null:
          this.selectablePaymentConfigs = [];
          break;
      }
    });

    this.selectedConfig.valueChanges.subscribe(value => {
      this.loadReportForPaymentConfig();
    });


    this.canUserImportData = this.context.checkUserFeatures(['accounts_trading_report_import'], this.offering!.offeringUuid);
  }

  loadOfferingPaymentConfigs() {
    this.paymentConfigsService.getDistrictPosConfigs().subscribe({
      next: value => {
        this.offeringPaymentConfigs = value;
        this.paymentConfigType.setValue('OFFERING');
      },
      error: err => console.log(err)
    })
  }

  loadSharedLeasePaymentConfigs() {
    this.reportService.getSharedLeasePaymentConfigs().subscribe({
      next: value => {
        this.sharedPaymentConfigs = value;
      },
      error: err => console.log(err)
    })
  }

  loadReportForPaymentConfig() {
    if (this.selectedConfig.value) {
      this.importMessage = 'Loading report for selected config';
      this.loadRetailerReport()
        .then(() => this.loadTotalReport()
          .then(() => {
            this.importMessage = null;
            this.showTables = true;
            this.refreshMessage = `The latest sale date found is ${this.dateUtils.displayDateWithTime(this.latestDate)}. If the data displayed is not up to date, click the "Refresh" button to request the latest trading data.`;
          }));
    }
  }

  loadRetailerReport() {
    return lastValueFrom(this.reportService.getRetailerTradingReportForPaymentConfig(
      this.selectedConfig.value!,
      this.dateUtils.displayShortDate(this.dateRange.start),
      this.dateUtils.displayShortDate(this.dateRange.end)))
      .then(value => {
        this.retailerTradingReport = {...value};
      }, error => console.log(error))
  }

  loadTotalReport() {
    return lastValueFrom(this.reportService.getTotalTradingReportForPaymentConfig(
      this.selectedConfig.value!,
      this.dateUtils.displayShortDate(this.dateRange.start),
      this.dateUtils.displayShortDate(this.dateRange.end)
    ))
      .then(value => {
        this.totalTradingReport = {...value};
        this.tabs[1].badge = this.errorsToFix ? this.totalTradingReport.errorTradingReport.length : null;
        this.tabs[1].disabled = !this.errorsToFix;
        const allDates = this.totalTradingReport.totalReport.map(m => new Date(m.transactionDate).getTime());
        this.latestDate = new Date(Math.max(...allDates));
      }, error => console.log(error))
  }

  loadImportConfig() {
    this.payoutsConfigService.getDefaultImportConfig().subscribe({
      next: value => {
        this.defaultImportConfig = {...value};
      },
      error: err => console.log(err)
    })
  }

  importTransactionsForConfig() {
    if (this.selectedConfig.value) {
      this.importMessage = 'Importing trading data for selected config. Please allow a few minutes for the data to be processed.';
      const request: ImportNewTransactionsConfig = {...this.defaultImportConfig!};

      request.startDate = null;
      request.endDate = this.dateUtils.displayShortDate(new Date())!;
      request.paymentConfigUuid = this.selectedConfig.value;

      // ensure that the selected transaction source is enabled for the import
      if (request.transactionSources.length > 0) {
        switch (this.paymentConfigType.value) {
          case "OFFERING":
            if (!request.transactionSources.includes(TransactionSource.VEND_SUPPLIER_OFFERING)) {
              request.transactionSources.push(TransactionSource.VEND_SUPPLIER_OFFERING);
            }
            break;
          case "SHARED":
            if (!request.transactionSources.includes(TransactionSource.VEND_SUPPLIER_LEASE)) {
              request.transactionSources.push(TransactionSource.VEND_SUPPLIER_LEASE);
            }
            break;
          default:
            break;
        }
      }

      this.reportService.importNewTransactions(request)
        .subscribe({
          next: value => {
            this.importMessage = 'Loading report for selected config';
            this.loadReportForPaymentConfig();
          },
          error: err => {
            this.importMessage = 'Could not import trading data for selected config';
            console.log(err);
          }
        });
    }
  }

  importProductsAndTransactionsForConfig() {
    if (this.selectedConfig.value) {
      this.importMessage = 'Fixing product data for selected config. Please allow a few minutes for the data to be processed.';
      this.reportService.importNewProducts(this.selectedConfig.value)
        .subscribe({
          next: value => {
            this.importTransactionsForConfig();
          },
          error: err => {
            this.importMessage = 'Could not fix product data for selected config';
            console.log(err);
          }
        });
    }
  }

  getDisplayName(config: PaymentConfigRequest) {
    const displayName = this.getConfigValue('displayName', config.transactionSourceConfig!);
    if (!displayName || displayName.trim().length == 0) {
      return this.getConfigValue('domainPrefix', config.transactionSourceConfig!);
    }
    return displayName;
  }

  getConfigValue(key: string, providerConfig: string) {
    const json = JSON.parse(providerConfig);
    const keys = key.split('.')
    let result = json;
    for (const key of keys) {
      if (result.hasOwnProperty(key)) {
        result = result[key];
      } else {
        return undefined; // or throw an error, or handle the case as needed
      }
    }
    return result;
  }

  getDateRange(event: CustomDateRangeChangeEventModel) {
    if (event.dateRange.start && event.dateRange.end) {
      this.dateRange = event.dateRange;
      this.loadReportForPaymentConfig();
    }
  }

  changeTabs($event: PageHeaderTab): void {
    this.activeTab = $event.code;
  }

  getInitialTab(): PageHeaderTab {
    return this.tabs.at(0)!;
  }

  get configFiltersNotSet() {
    return this.selectedConfig.value == null || this.dateRange.start == null || this.dateRange.end == null;
  }

  get errorsToFix() {
    return !!this.totalTradingReport && this.totalTradingReport.errorTradingReport.length > 0;
  }

  // DAILY TRADING REPORT

  // filter at top of page for payment method, retailer and dates
  // total report
  // full retailer report


  // CASH TRADING REPORT

  // filter at top of page for retailer and dates
  // total report
  // full retailer report filtered by payment method = 'CASH'

  protected readonly PanelType = PanelType;
}
